export const ORPHAN_GAP_STRONG_POLICY_CONTRACT_VERSION = 1;
export const ORPHAN_GAP_STRONG_MAX_PREMIUM_PCT = 20;

export const ORPHAN_GAP_STRONG_INVARIANT_FIELDS = Object.freeze([
  "last_room_required",
  "strong_pace_required",
  "no_longer_stay_displacement_required",
  "restrictions_unchanged_required",
  "zero_discount_required",
  "consultative_only",
]);

const LOW_CONFIDENCE_ACTIONS = new Set(["hold", "require_approval"]);

function normalizePositiveInteger(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizePercent(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? String(Number((numeric * 100).toFixed(4)))
    : String(fallback);
}

function normalizeStrategies(strategies) {
  return (Array.isArray(strategies) ? strategies : [])
    .map((strategy) => ({
      id: normalizePositiveInteger(strategy?.id),
      scope_type: String(strategy?.scope_type || "hotel"),
      scope_id: strategy?.scope_id ?? null,
      strategy_mode: String(strategy?.strategy_mode || "balanced"),
      is_active: strategy?.is_active === true,
    }))
    .filter((strategy) => strategy.id != null)
    .sort((left, right) => left.id - right.id);
}

export function normalizeOrphanGapStrongPolicyDraft(runtime = {}, strategies = []) {
  const runtimeItems = Array.isArray(runtime?.items) ? runtime.items : [];
  const categoryResolutionById = new Map(
    (Array.isArray(runtime?.category_resolutions)
      ? runtime.category_resolutions
      : []
    )
      .map((resolution) => [
        normalizePositiveInteger(resolution?.room_category_id),
        resolution,
      ])
      .filter(([roomCategoryId]) => roomCategoryId != null)
  );
  const itemByStrategyId = new Map(
    runtimeItems
      .map((item) => [normalizePositiveInteger(item?.strategy_setting_id), item])
      .filter(([strategyId]) => strategyId != null)
  );
  const normalizedStrategies = normalizeStrategies(strategies);
  const hotelStrategy = normalizedStrategies.find(
    (strategy) => strategy.scope_type === "hotel" && strategy.scope_id == null
  );
  const hotelSource = hotelStrategy
    ? itemByStrategyId.get(hotelStrategy.id) || null
    : null;

  return {
    contract_version: Number(runtime?.contract_version || 0),
    business_unit_id: normalizePositiveInteger(runtime?.business_unit_id),
    expected_configuration_hash:
      typeof runtime?.configuration_hash === "string" && runtime.configuration_hash.trim()
        ? runtime.configuration_hash.trim()
        : null,
    items: normalizedStrategies.map((strategy) => {
      const explicitSource = itemByStrategyId.get(strategy.id) || null;
      const source =
        explicitSource ||
        (strategy.scope_type === "room_category" ? hotelSource : null) ||
        {};
      const categoryResolution =
        strategy.scope_type === "room_category"
          ? categoryResolutionById.get(normalizePositiveInteger(strategy.scope_id)) ||
            null
          : null;
      const dependencyMaskCodes = Array.isArray(
        categoryResolution?.dependency_mask_codes
      )
        ? categoryResolution.dependency_mask_codes
            .map((code) => String(code || "").trim())
            .filter(Boolean)
        : [];
      const item = {
        strategy_setting_id: strategy.id,
        // UI-only provenance used to preserve explicit category opt-outs when
        // resolving hotel inheritance. It is deliberately absent from PUTs.
        policy_is_configured: explicitSource != null,
        scope_type: strategy.scope_type,
        scope_id: strategy.scope_id,
        strategy_mode: strategy.strategy_mode,
        strategy_is_active: strategy.is_active,
        is_enabled: source?.is_enabled === true,
        // Read-only backend resolution. Keep it distinct from `is_enabled`,
        // which is the configured/inherited value edited by this form.
        effective_state_available: categoryResolution != null,
        effective_is_enabled:
          categoryResolution == null
            ? null
            : categoryResolution?.is_enabled === true,
        dependency_masked:
          categoryResolution?.dependency_masked === true,
        dependency_mask_codes: dependencyMaskCodes,
        max_premium_pct: normalizePercent(source?.max_premium_pct, 0),
        low_confidence_action: LOW_CONFIDENCE_ACTIONS.has(
          String(source?.low_confidence_action || "")
        )
          ? String(source.low_confidence_action)
          : "require_approval",
      };
      ORPHAN_GAP_STRONG_INVARIANT_FIELDS.forEach((field) => {
        item[field] = source?.[field] == null ? true : source[field] === true;
      });
      return item;
    }),
  };
}

export function updateOrphanGapStrongPolicyDraftItem(
  draft,
  strategySettingId,
  field,
  value
) {
  const strategyId = normalizePositiveInteger(strategySettingId);
  if (
    strategyId == null ||
    ORPHAN_GAP_STRONG_INVARIANT_FIELDS.includes(field) ||
    !["is_enabled", "max_premium_pct", "low_confidence_action"].includes(field)
  ) {
    return draft;
  }
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const target = items.find(
    (item) => Number(item?.strategy_setting_id) === strategyId
  );
  if (!target) return draft;
  let updatedTarget;
  if (field === "is_enabled") {
    updatedTarget =
      value === true
        ? { ...target, policy_is_configured: true, is_enabled: true }
        : {
            ...target,
            policy_is_configured: true,
            is_enabled: false,
            max_premium_pct: "0",
          };
  } else {
    updatedTarget = {
      ...target,
      policy_is_configured: true,
      [field]: String(value ?? ""),
    };
  }
  const updatesInheritedCategories =
    target.scope_type === "hotel" && target.scope_id == null;
  return {
    ...(draft || {}),
    items: items.map((item) => {
      if (Number(item?.strategy_setting_id) === strategyId) {
        return updatedTarget;
      }
      if (
        !updatesInheritedCategories ||
        item?.scope_type !== "room_category" ||
        item?.policy_is_configured !== false
      ) {
        return item;
      }
      return ORPHAN_GAP_STRONG_INVARIANT_FIELDS.reduce(
        (next, invariant) => ({
          ...next,
          [invariant]: updatedTarget[invariant],
        }),
        {
          ...item,
          is_enabled: updatedTarget.is_enabled,
          max_premium_pct: updatedTarget.max_premium_pct,
          low_confidence_action: updatedTarget.low_confidence_action,
        }
      );
    }),
  };
}

function configuredPolicyItems(draft) {
  return (Array.isArray(draft?.items) ? draft.items : []).filter(
    (item) => item?.policy_is_configured !== false
  );
}

export function resolveInheritedStrategyPolicy(draft, strategyItem) {
  const items = configuredPolicyItems(draft);
  if (strategyItem?.scope_type === "room_category") {
    const categoryPolicy = items.find(
      (item) =>
        item?.scope_type === "room_category" &&
        Number(item?.scope_id) === Number(strategyItem?.scope_id)
    );
    if (categoryPolicy) return categoryPolicy;
  }
  return (
    items.find(
      (item) => item?.scope_type === "hotel" && item?.scope_id == null
    ) || null
  );
}

export function validateOrphanGapStrongPolicyDraft(
  draft,
  horizonPolicyDraft = null,
  nearPolicyDraft = null
) {
  const errors = [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const seen = new Set();

  items.forEach((item) => {
    const strategyId = normalizePositiveInteger(item?.strategy_setting_id);
    if (strategyId == null || seen.has(strategyId)) {
      errors.push("Each strong-demand strategy must appear exactly once.");
    } else {
      seen.add(strategyId);
    }
    const premium = Number(item?.max_premium_pct);
    if (!Number.isFinite(premium) || premium < 0 || premium > 20) {
      errors.push("The strong-demand premium cap must stay between 0 and 20%.");
    }
    if (!LOW_CONFIDENCE_ACTIONS.has(String(item?.low_confidence_action || ""))) {
      errors.push("Choose hold or require approval for low confidence.");
    }
    if (ORPHAN_GAP_STRONG_INVARIANT_FIELDS.some((field) => item?.[field] !== true)) {
      errors.push("Strong-demand safety invariants must remain enabled.");
    }
    if (item?.is_enabled !== true && premium !== 0) {
      errors.push("A disabled strong-demand policy cannot retain a premium cap.");
    }
    if (item?.is_enabled === true && item?.policy_is_configured !== false) {
      if (
        horizonPolicyDraft &&
        resolveInheritedStrategyPolicy(horizonPolicyDraft, item)?.is_enabled !== true
      ) {
        errors.push("Enable the matching T07 orphan-gap policy first.");
      }
      if (
        nearPolicyDraft &&
        resolveInheritedStrategyPolicy(nearPolicyDraft, item)?.is_enabled !== true
      ) {
        errors.push("Enable the matching T08 near-gap policy first.");
      }
    }
  });

  return [...new Set(errors)];
}

function percentToRatio(value) {
  return Number((Number(value) / 100).toFixed(6));
}

export function buildOrphanGapStrongPolicyPayload(draft) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (Array.isArray(draft?.items) ? draft.items : [])
      .filter((item) => item?.policy_is_configured !== false)
      .map((item) => ({
        strategy_setting_id: Number(item.strategy_setting_id),
        is_enabled: item.is_enabled === true,
        max_premium_pct: percentToRatio(item.max_premium_pct),
        low_confidence_action: item.low_confidence_action,
      })),
  };
}
